import React, { useMemo } from 'react';
import { Box } from '@mui/material';

import type { EligibilityRule } from '../../../types/admissions';
import { checkAge } from '../../../utils/age';
import { fmtClassName } from '../../../utils/pdf/format';

/**
 * The line under "Date of birth": the child's age on the class's cut-off
 * date, and — when it falls outside the class's age limit — what the limit
 * is, so the person filling the form sees it before submitting rather than
 * after. Returns hints keyed by field, ready for DynamicFieldRenderer.
 */
export function useDobHint(
  eligibility: Array<Pick<EligibilityRule, 'classId' | 'ageOn'>> | undefined,
  classOptions: Array<{ _id: string; name: string }>,
  classId?: string,
  dob?: string | null,
): Partial<Record<string, React.ReactNode>> | undefined {
  return useMemo(() => {
    const check = checkAge(eligibility, classId, dob);
    if (!check) return undefined;
    const age = `${check.age} on ${check.on}`;
    if (check.fits) return { dob: age };
    const cls = classOptions.find(c => c._id === classId)?.name;
    return {
      dob: (
        <Box component="span" sx={{ color: 'warning.dark' }}>
          {age} · {cls ? fmtClassName(cls) : 'This class'} is for ages {check.limit}
        </Box>
      ),
    };
  }, [eligibility, classOptions, classId, dob]);
}
